<template>
    <div class="traceability-table" :class="$mq">
        <div v-if="loading" class="traceability-table__loading" aria-live="polite"></div>
        <EmptyPage
            v-else-if="offline"
            :title="$t('empty_pages.connection_description')"
            :description="$t('empty_pages.connection_title')"
            class="error"
        />
        <EmptyPage
            v-else-if="hasApiError"
            type="error"
            :title="$t('empty_pages.api_error_title')"
            :description="$t('empty_pages.api_error_description')"
        />
        <div v-else-if="!hasRows" class="empty-filter-state">
            <EmptyPage :title="$t('empty_pages.generic_title')" :description="$t('empty_pages.label_description')" />
            <button v-if="showClearFiltersAction" type="button" class="clear-filters-button" @click="$emit('clear-filters')">
                <i class="i-andy-refresh" aria-hidden="true"></i>
                {{ $t('assets.filter.delete') }}
            </button>
        </div>
        <BaseTable
            v-else
            compactPagination
            :headers="headers"
            :rows="rows"
            :totalItems="normalizedTotalItems"
            :pageNumber="pageNumber"
            :itemsPerPage="normalizedItemsPerPage"
            showPageNumbers
            showTotal
            :totalLabel="totalLabel"
            :columnStyles="columnStyles"
            :rowClass="getRowClass"
            @row-click="openLabel"
            @back-button-click="$emit('previous-page')"
            @forward-button-click="$emit('next-page')"
            @page-button-click="$emit('page-change', $event)"
        >
            <template #cell-product="{ value, row }">
                <div class="table-cell table-cell--product" :class="expirationClass(row)">
                    <span class="mobile-cell-label">{{ headers[0].text }}</span>
                    <span class="product-name" :title="value">{{ value }}</span>
                </div>
            </template>

            <template #cell-status="{ row }">
                <div class="table-cell table-cell--status">
                    <span class="mobile-cell-label">{{ headers[1].text }}</span>
                    <div class="status-tags">
                        <span v-if="row.status" class="state-tag" :title="row.status">{{ row.status }}</span>
                        <span v-else class="state-placeholder">-</span>
                    </div>
                </div>
            </template>

            <template #cell-quantity="{ row }">
                <div class="table-cell table-cell--quantity">
                    <span class="mobile-cell-label">{{ headers[2].text }}</span>
                    <div class="quantity-tags">
                        <span
                            v-if="showsTimer(row.traceabilityKind)"
                            class="quantity-tag"
                            :aria-label="`${$t('label.traceability.record_type_timer')}: ${row.timerQuantity}`"
                        >
                            <i class="i-andy-menu-timer i-andy-s" aria-hidden="true"></i>
                            <span>{{ row.timerQuantity }}</span>
                        </span>
                        <span
                            v-if="showsPhysical(row.traceabilityKind)"
                            class="quantity-tag"
                            :aria-label="`${$t('label.traceability.record_type_physical')}: ${row.physicalQuantity}`"
                        >
                            <span class="physical-label-icon" aria-hidden="true"></span>
                            <span>{{ row.physicalQuantity }}</span>
                        </span>
                        <span v-if="!showsTimer(row.traceabilityKind) && !showsPhysical(row.traceabilityKind)" class="state-placeholder">-</span>
                    </div>
                </div>
            </template>

            <template #cell-expirationDate="{ value, row }">
                <div class="table-cell table-cell--expiration" :class="expirationClass(row)">
                    <span class="mobile-cell-label">{{ headers[3].text }}</span>
                    <span class="expiration-value">{{ value }}</span>
                </div>
            </template>

            <template #cell-labelAction="{ row }">
                <div class="table-cell table-cell--action">
                    <span class="mobile-cell-label">{{ headers[4].text }}</span>
                    <button
                        type="button"
                        class="preview-action"
                        :aria-label="$t('label.traceability.open_label')"
                        :title="$t('label.traceability.open_label')"
                        @click.stop="openLabel(row)"
                    ></button>
                </div>
            </template>
        </BaseTable>
    </div>
</template>

<script>
import BaseTable from '@/components/domain/ui/BaseTable.vue'
import EmptyPage from '@/components/layout/EmptyPage.vue'
import {
    isTraceabilityClosingDate,
    parseTraceabilityMoment,
    resolveTraceabilityExpirationState
} from '@/utils/traceabilityExpiration'
import { normalizeItemsPerPage, TRACEABILITY_ITEMS_PER_PAGE } from '@/utils/pagination'

export default {
    name: 'TraceabilityTable',
    components: { BaseTable, EmptyPage },
    props: {
        labels: {
            type: Array,
            default: () => []
        },
        totalItems: {
            type: [Number, String],
            default: 0
        },
        totalLabel: {
            type: String,
            default: ''
        },
        pageNumber: {
            type: Number,
            default: 1
        },
        itemsPerPage: {
            type: Number,
            default: TRACEABILITY_ITEMS_PER_PAGE
        },
        showBy: {
            type: String,
            default: 'expiration_date'
        },
        loading: {
            type: Boolean,
            default: false
        },
        offline: {
            type: Boolean,
            default: false
        },
        hasApiError: {
            type: Boolean,
            default: false
        },
        showClearFiltersAction: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        headers() {
            return [
                { text: this.$t('label.traceability.product').toUpperCase(), key: 'product' },
                { text: this.$t('label.traceability.status').toUpperCase(), key: 'status' },
                { text: this.$t('label.traceability.quantity').toUpperCase(), key: 'quantity' },
                {
                    text: this.$t(this.showBy === 'print_date' ? 'label.preview.print_date' : 'label.traceability.expiration_date').toUpperCase(),
                    key: 'expirationDate'
                },
                { text: this.$t('label.traceability.label').toUpperCase(), key: 'labelAction' }
            ]
        },
        normalizedTotalItems() {
            const total = Number(this.totalItems)
            return Number.isFinite(total) ? total : 0
        },
        normalizedItemsPerPage() {
            return Math.min(normalizeItemsPerPage(this.itemsPerPage), TRACEABILITY_ITEMS_PER_PAGE)
        },
        productReferences() {
            return this.$store.getters['label/getLabelsOrdered'] || {}
        },
        serverNow() {
            const serverTimeDifference = Number(this.$store.getters['getServerTimeDifference']) || 0
            return moment().add(serverTimeDifference, 'minutes')
        },
        rows() {
            return this.labels.slice(0, this.normalizedItemsPerPage).map((label, index) => {
                const product = this.productReferences[label.product] || {}
                const state = this.$store.getters['label/getLabelStates'](label.state) || {}
                const parsedData = this.parseLabelData(label.data)
                const expirationSource = label.current_print_time || label.end_date || label.date
                const isClosing = this.showBy !== 'print_date' && this.isClosingDate(label.end_date)
                const expirationState = resolveTraceabilityExpirationState(label, this.serverNow, parsedData)
                const rowSource = label.trace_source || label.traceabilityKind || 'label'

                return {
                    id: `traceability-${rowSource}-${label.log_row_id || label.id || label.reference || 'label'}-${index}`,
                    label,
                    product: product.name || label.name || label.reference_name || '-',
                    status: state.name || '',
                    quantity: '',
                    physicalQuantity: this.normalizeQuantity(label.quantity, parsedData.quantity),
                    timerQuantity: this.normalizeQuantity(parsedData.quantity_avaibles, parsedData.quantity, label.quantity),
                    traceabilityKind: label.traceabilityKind,
                    expirationDate: isClosing ? this.$t('label.traceability.closing') : this.formatDate(expirationSource),
                    labelAction: '',
                    isClosing,
                    isExpired: expirationState.isExpired,
                    isExpiresToday: expirationState.isExpiresToday
                }
            })
        },
        hasRows() {
            return this.rows.length > 0
        },
        columnStyles() {
            return {
                product: 'traceability-product-column',
                status: 'traceability-status-column',
                quantity: 'traceability-quantity-column',
                expirationDate: 'traceability-expiration-column',
                labelAction: 'traceability-action-column'
            }
        }
    },
    methods: {
        parseLabelData(data) {
            if (!data) {
                return {}
            }
            if (typeof data === 'object') {
                return data
            }
            try {
                return JSON.parse(data)
            } catch (e) {
                return {}
            }
        },
        parseMoment(date) {
            return parseTraceabilityMoment(date)
        },
        formatDate(date) {
            const parsed = this.parseMoment(date)
            return parsed ? parsed.format('DD/MM/YYYY HH:mm') : date || '-'
        },
        isClosingDate(date) {
            return isTraceabilityClosingDate(date)
        },
        normalizeQuantity(...values) {
            const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '' && candidate !== 'NaN')
            const quantity = Number(value)
            return Number.isFinite(quantity) ? quantity : 0
        },
        showsPhysical(kind) {
            return kind === 'physical' || kind === 'both'
        },
        showsTimer(kind) {
            return kind === 'timer' || kind === 'both'
        },
        expirationClass(row) {
            return {
                'is-closing': row.isClosing,
                'is-expired': row.isExpired,
                'is-expiring-today': row.isExpiresToday
            }
        },
        getRowClass(row) {
            return this.expirationClass(row)
        },
        openLabel(row) {
            this.$emit('row-click', row.label)
        }
    }
}
</script>

<style lang="scss" scoped>
.traceability-table {
    position: relative;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;
    background-color: $default-bg-color;

    &__loading {
        width: 100%;
        height: 100%;
    }

    ::v-deep .base-table {
        table-layout: fixed;

        th,
        .cell {
            box-sizing: border-box;
            min-width: 0;
        }

        th:nth-child(1),
        .traceability-product-column {
            width: 25%;
        }

        th:nth-child(2),
        .traceability-status-column {
            width: 23%;
        }

        th:nth-child(3),
        .traceability-quantity-column {
            width: 18%;
        }

        th:nth-child(4),
        .traceability-expiration-column {
            width: 26%;
        }

        th:nth-child(5),
        .traceability-action-column {
            width: 8%;
            text-align: center;
        }

        .body .row {
            border-bottom: 1px solid $color-white;

            &.is-expired {
                background-color: $error-t90;
            }

            &.is-expiring-today {
                background-color: $color-white;
            }
        }
    }
}

.empty-filter-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    height: 100%;
    min-height: 0;

    ::v-deep .empty-container {
        flex: 0 1 auto;
        height: auto;
    }
}

.clear-filters-button {
    @include border-radius(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 166px;
    height: 36px;
    min-height: 36px;
    padding: 8px;
    border: 0;
    background-color: #edeff5;
    color: #374673;
    cursor: pointer;
    font-family: $text-bold;
    font-size: 18px;
    line-height: 18px;

    .i-andy-refresh {
        color: #374673;
        flex: 0 0 20px;
        width: 20px;
        height: 20px;
        font-size: 20px;
        line-height: 20px;
    }
}

.table-cell {
    min-width: 0;
    color: $neutro-s80;

    &--product,
    &--expiration {
        font-family: $text;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;

        &.is-expired {
            display: flex;
            align-items: center;
            color: #bd1f1e;
            font-family: $text-bold;
            font-weight: 700;
            text-transform: uppercase;
            transform: rotate(0.08deg);
        }

        &.is-expiring-today {
            color: $warning;
            font-family: $text-bold;
            font-weight: 700;
            text-transform: uppercase;
        }
    }

    &--status,
    &--quantity,
    &--action {
        display: flex;
        align-items: center;
    }

    &--action {
        justify-content: center;
    }
}

.product-name,
.expiration-value {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.table-cell--product.is-expired .product-name,
.table-cell--expiration.is-expired .expiration-value {
    display: flex;
    align-items: center;
    color: #bd1f1e;
    font-family: $text-bold;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    text-transform: uppercase;
    transform: rotate(0.08deg);
}

.table-cell--product.is-expiring-today .product-name,
.table-cell--expiration.is-expiring-today .expiration-value {
    color: $warning;
    font-family: $text-bold;
    font-weight: 700;
    text-transform: uppercase;
}

.table-cell--expiration.is-closing .expiration-value {
    display: flex;
    align-items: center;
    color: #cd902f;
    font-family: $text-bold;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    text-transform: uppercase;
    transform: rotate(0.08deg);
}

.status-tags,
.quantity-tags {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex-wrap: wrap;
}

.state-tag,
.quantity-tag {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    height: 28px;
    border-radius: 4px;
    white-space: nowrap;
}

.state-tag {
    min-width: 0;
    padding: 4px 8px;
    color: $neutro-s80;
    font-family: $text-light;
    font-size: 14px;
    line-height: 20px;
}

.state-tag {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: #edeff5;
}

.quantity-tag {
    gap: 4px;
    padding: 2px 5px;
    border: 1px solid $neutro-dark;
    background-color: $color-white;
    color: $neutro-s80;
    font-family: $text-light;
    font-size: 14px;
    line-height: 20px;

    i {
        color: $neutro-s30;
        font-size: 18px;
        line-height: 20px;
    }

    .physical-label-icon {
        @include background($image: img('menu_label_dark.svg'), $position: center, $size: contain);
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }
}

.state-placeholder {
    color: $neutro-s50;
}

.preview-action {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    @include background($image: img('eye_preview_blue.svg'), $color: $main-t90, $size: 20px, $position: center center);
}

.mobile-cell-label {
    display: none;
}

@media (max-width: 767px) {
    .traceability-table {
        display: flex;
        flex-direction: column;
        overflow: hidden;

        ::v-deep .base-table-container {
            height: 100%;
            min-height: 0;
            overflow: hidden;
        }

        ::v-deep .table-wrapper {
            flex: 1 1 auto;
            height: auto;
            min-height: 0;
            overflow-x: hidden;
            overflow-y: auto;
            padding-bottom: 0;

            &.has-pagination {
                height: calc(100% - 48px);
                max-height: calc(100% - 48px);
            }
        }

        ::v-deep .base-table,
        ::v-deep .base-table .body {
            display: block;
            width: 100%;
        }

        ::v-deep .base-table .headers {
            display: block;
            position: sticky;
            top: 0;
            width: 100%;
            height: auto;

            tr {
                display: grid;
                grid-template-columns: 28% 30% minmax(0, 1fr) 48px;
                width: 100%;
                min-height: 58px;
            }

            th {
                display: flex;
                align-items: center;
                box-sizing: border-box;
                width: auto;
                min-width: 0;
                max-width: none;
                height: auto;
                padding: 8px;
                overflow: visible;
                white-space: normal;
                text-overflow: clip;

                &:nth-child(1) {
                    grid-column: 1;
                }

                &:nth-child(2) {
                    grid-column: 2;
                }

                &:nth-child(3) {
                    display: none;
                }

                &:nth-child(4) {
                    grid-column: 3;
                }

                &:nth-child(5) {
                    grid-column: 4;

                    .header-text {
                        display: none;
                    }
                }

                .header-content,
                .header-text {
                    overflow: visible;
                    white-space: normal;
                    text-overflow: clip;
                }
            }

            .header-content .header-text {
                overflow: visible;
                white-space: normal;
                text-overflow: clip;
            }
        }

        ::v-deep .base-table .body .row {
            display: grid;
            grid-template-columns: 28% 30% minmax(0, 1fr) 48px;
            grid-template-rows: auto auto;
            column-gap: 0;
            row-gap: 4px;
            width: 100%;
            min-height: 0;
            padding: 12px 0;
            margin: 0;
            border: 0;
            border-bottom: 1px solid $color-white;
            border-radius: 0;
            overflow: visible;
        }

        ::v-deep .base-table .body .cell {
            display: block;
            width: auto;
            min-width: 0;
            height: auto;
            padding: 6px;
        }

        ::v-deep .traceability-product-column {
            grid-column: 1;
            grid-row: 1 / 3;
            align-self: center;
        }

        ::v-deep .base-table .body .cell.traceability-status-column {
            grid-column: 2;
            grid-row: 1;
            align-self: end;
            padding: 0;
        }

        ::v-deep .base-table .body .cell.traceability-quantity-column {
            grid-column: 2;
            grid-row: 2;
            align-self: start;
            padding: 0;
        }

        ::v-deep .traceability-expiration-column {
            grid-column: 3;
            grid-row: 1 / 3;
            align-self: center;
        }

        ::v-deep .traceability-action-column {
            grid-column: 4;
            grid-row: 1 / 3;
            align-self: center;
            padding-right: 4px;
            padding-left: 2px;
        }

        ::v-deep .pagination-container.detailed {
            height: 48px;
            min-height: 48px;
        }
    }

    .table-cell {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;

        &--status,
        &--quantity {
            height: auto;
        }

        &--action {
            display: flex;
            justify-content: center;
        }

        &--product,
        &--expiration {
            font-size: 16px;
            line-height: 20px;
        }
    }

    .mobile-cell-label {
        display: none;
    }

    .status-tags,
    .quantity-tags {
        justify-content: flex-start;
    }

    .state-tag,
    .quantity-tag {
        font-size: 16px;
        line-height: 20px;
    }

    .product-name,
    .expiration-value {
        white-space: normal;
        overflow-wrap: normal;
        overflow: visible;
        text-overflow: clip;
    }

    .product-name {
        overflow-wrap: anywhere;
    }

    .expiration-value {
        white-space: normal;
    }
}
</style>
